import * as React from "react"

const MOBILE_BREAKPOINT = 768
/** Below this, pages use their stacked / single-pane layouts (phones + iPad portrait). */
export const COMPACT_BREAKPOINT = 1024

function readQuery(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia(query).matches
}

/**
 * Live result of a CSS media query. Initialised synchronously from matchMedia
 * so the first render already has the right layout (no desktop flash on phones).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState<boolean>(() => readQuery(query))

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener("change", onChange)
    return () => mql.removeEventListener("change", onChange)
  }, [query])

  return matches
}

/** Phones (below 768px): the sidebar becomes a slide-out sheet. */
export function useIsMobile() {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
}

/** Phones and iPad portrait (below 1024px): single-pane Email, day-view Calendar, card lists. */
export function useIsCompact() {
  return useMediaQuery(`(max-width: ${COMPACT_BREAKPOINT - 1}px)`)
}

/** Touch-first device (no precise pointer): show ⋯ buttons, larger targets. */
export function useIsCoarsePointer() {
  return useMediaQuery("(pointer: coarse)")
}
